// AdminLayout — sidebar ADMIN AuthSystem (13 trang). Permission-filter qua usePermissions().has(perm).
// Header có nút "← FMS" để navigate / (về AppLayout business).

import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  DashboardOutlined, UserOutlined, TeamOutlined, KeyOutlined, SafetyOutlined,
  AuditOutlined, AlertOutlined, IdcardOutlined, DesktopOutlined,
  BgColorsOutlined, MailOutlined, GoogleOutlined, LockOutlined,
  AppstoreOutlined, CustomerServiceOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@zero/auth-client';
import { FMSLayout, filterByPerm } from '@fms/shared';

const ROUTE_TO_KEY = {
  '/admin':              'dashboard',
  '/admin/':             'dashboard',
  '/admin/users':        'users',
  '/admin/roles':        'roles',
  '/admin/permissions':  'permissions',
  '/admin/policies':     'policies',
  '/admin/audit':        'audit',
  '/admin/alerts':       'alerts',
  '/admin/sessions':     'sessions',
  '/admin/profile':      'profile',
  '/admin/branding':     'branding',
  '/admin/smtp':         'smtp',
  '/admin/google-oauth': 'googleOAuth',
  '/admin/security':     'security',
  '/admin/ticket':       'ticketConfig',
};

export function AdminLayout() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const location = useLocation();
  const selectedKey = ROUTE_TO_KEY[location.pathname] ?? 'dashboard';

  const sidebarItemsRaw = useMemo(() => [
    { key: 'dashboard', perm: 'Auth.Audit.ViewStats', icon: <DashboardOutlined />, label: <Link to="/admin">{t('menu.dashboard')}</Link> },
    {
      key: 'system', icon: <KeyOutlined />, label: t('menu.system'),
      children: [
        { key: 'users',       perm: 'Auth.User.View',       icon: <UserOutlined />,   label: <Link to="/admin/users">{t('menu.users')}</Link> },
        { key: 'roles',       perm: 'Auth.Role.View',       icon: <TeamOutlined />,   label: <Link to="/admin/roles">{t('menu.roles')}</Link> },
        { key: 'permissions', perm: 'Auth.Permission.View', icon: <KeyOutlined />,    label: <Link to="/admin/permissions">{t('menu.permissions')}</Link> },
        { key: 'policies',    perm: 'Auth.Policy.View',     icon: <SafetyOutlined />, label: <Link to="/admin/policies">{t('menu.policies')}</Link> },
      ],
    },
    {
      key: 'monitoring', icon: <AppstoreOutlined />, label: t('menu.monitoring'),
      children: [
        { key: 'audit',  perm: 'Auth.Audit.View', icon: <AuditOutlined />, label: <Link to="/admin/audit">{t('menu.audit')}</Link> },
        { key: 'alerts', perm: 'Auth.Alert.View', icon: <AlertOutlined />, label: <Link to="/admin/alerts">{t('menu.alerts')}</Link> },
      ],
    },
    {
      key: 'config', icon: <BgColorsOutlined />, label: t('menu.config'),
      children: [
        { key: 'branding',    perm: 'Auth.Config.View', icon: <BgColorsOutlined />, label: <Link to="/admin/branding">{t('menu.branding')}</Link> },
        { key: 'smtp',        perm: 'Auth.Config.View', icon: <MailOutlined />,     label: <Link to="/admin/smtp">{t('menu.smtp')}</Link> },
        { key: 'googleOAuth', perm: 'Auth.Config.View', icon: <GoogleOutlined />,   label: <Link to="/admin/google-oauth">{t('menu.googleOAuth')}</Link> },
        { key: 'security',    perm: 'Auth.Config.View', icon: <LockOutlined />,     label: <Link to="/admin/security">{t('menu.security')}</Link> },
        { key: 'ticketConfig', perm: 'FMS.MAST.TicketConfig.View', icon: <CustomerServiceOutlined />, label: <Link to="/admin/ticket">{t('menu.ticketConfig', 'Hệ thống hỗ trợ')}</Link> },
      ],
    },
    {
      key: 'account', icon: <IdcardOutlined />, label: t('menu.account'),
      children: [
        { key: 'profile',  icon: <IdcardOutlined />, label: <Link to="/admin/profile">{t('menu.profile')}</Link> },
        { key: 'sessions', icon: <DesktopOutlined />, label: <Link to="/admin/sessions">{t('menu.sessions')}</Link> },
      ],
    },
  ], [t]);

  const sidebarItems = useMemo(() => filterByPerm(sidebarItemsRaw, has), [sidebarItemsRaw, has]);

  const labels = {
    dashboard: t('menu.dashboard'),
    users: t('menu.users'), roles: t('menu.roles'),
    permissions: t('menu.permissions'), policies: t('menu.policies'),
    audit: t('menu.audit'), alerts: t('menu.alerts'),
    sessions: t('menu.sessions'), profile: t('menu.profile'),
    branding: t('menu.branding'), smtp: t('menu.smtp'),
    googleOAuth: t('menu.googleOAuth'), security: t('menu.security'),
    ticketConfig: t('menu.ticketConfig', 'Hệ thống hỗ trợ'),
  };
  const headerTitle = `${t('menu.admin', 'Quản trị')} / ${labels[selectedKey] || ''}`;

  return (
    <FMSLayout
      sidebarItems={sidebarItems}
      selectedKey={selectedKey}
      defaultOpenKeys={['system', 'monitoring', 'config', 'account']}
      headerTitle={headerTitle}
      moduleName={t('menu.admin', 'Quản trị')}
      currentModuleKey="admin"
    />
  );
}
